/**
 * Account-level AI settings — the "AI" section of the account settings panel.
 *
 * Two blocks: per-mode default models (four selects persisted via
 * `PATCH /account/ai-settings`, applying to every project the user owns unless
 * the project overrides them) and the user's own custom ("bring your own AI")
 * providers (the shared {@link AiProviderManager} against
 * `/account/ai-provider`). The unscoped models catalog already includes the
 * session user's custom models, so the selects offer them with the same
 * per-option gating as the project settings modal: custom models on every
 * tier, non-free platform models pro-only.
 *
 * @module
 */

import { type JSX, useEffect, useState } from 'react'

import { resetAIModelsCache, useAIModels, useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Select } from '@molecule/app-ui-react'

import { AiProviderManager, ModelsCatalogRefetch } from './AiProviderManager.js'

/** The account-level model defaults returned by `GET /account/ai-settings`. */
interface AccountAiSettings {
  planModel?: string
  executeModel?: string
  commitModel?: string
  compactModel?: string
  /** Per-MODE processing-region choices — one per row, so changing e.g. the
   * execute region never moves the commit/compaction rows even when they run
   * the same model. */
  planModelRegion?: string
  executeModelRegion?: string
  commitModelRegion?: string
  compactModelRegion?: string
  /** Legacy per-model region map (modelId → region code) — read fallback for
   * choices saved before regions became per-mode; no longer written. */
  modelRegions?: Record<string, string>
}

/** The per-mode model setting keys (each patched with its own region key). */
type ModelSettingKey = 'planModel' | 'executeModel' | 'commitModel' | 'compactModel'
/** The per-mode region key for a model key (`planModel` → `planModelRegion`). */
type ModelRegionKey = `${ModelSettingKey}Region`

/**
 * Region-qualified option values encode `modelId|region`. `|` cannot appear in
 * a platform id, and the custom-provider models textarea uses `|` as its own
 * field separator, so no custom model id can contain it either.
 */
const REGION_SEP = '|'

/**
 * Renders the account-level AI section: default-model selects + the
 * bring-your-own provider manager.
 *
 * @param props - Component props.
 * @param props.isPro - Whether the user has a paid plan (gates non-free
 *   platform model options; custom models stay selectable on every tier).
 * @returns The rendered section body (the host renders the section heading).
 */
export function AccountAiSection({ isPro }: { isPro: boolean }): JSX.Element {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  // Unscoped: platform models + the session user's own custom models.
  // `defaults` = the tier's per-mode fallback ids, so each row can name what
  // "Default" resolves to.
  const { models, defaults: serverDefaults } = useAIModels()

  const [aiSettings, setAiSettings] = useState<AccountAiSettings>({})
  // Bumped after a provider save/delete: drops the cached models catalog and
  // remounts the refetcher below so the selects pick up the change.
  const [modelsEpoch, setModelsEpoch] = useState(0)
  const handleProvidersChanged = (): void => {
    resetAIModelsCache()
    setModelsEpoch((n) => n + 1)
  }

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await http.get<{ aiSettings?: AccountAiSettings }>('/account/ai-settings')
        if (!cancelled) setAiSettings(res.data.aiSettings ?? {})
      } catch (error) {
        console.error('Failed to load account AI settings', error)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [http])

  /** Persist a partial defaults patch + update local state optimistically. */
  const patchAiSettings = (patch: Partial<AccountAiSettings>): void => {
    setAiSettings((prev) => ({ ...prev, ...patch }))
    http.patch('/account/ai-settings', patch).catch((error: unknown) => {
      console.error('Failed to update account AI settings', error)
    })
  }

  /**
   * "Use" on a provider's model row. The account panel has no single "chat
   * model" (it is strictly per-mode), so this makes the model the default for
   * the two PRIMARY modes the user actively drives — plan + execute — leaving
   * the cheap commit/compact aux models alone. Applies to every project the user
   * owns unless a project overrides it. Regions are cleared (a custom model has
   * none).
   */
  const handleUseModel = (customModelId: string): void => {
    patchAiSettings({
      planModel: customModelId,
      planModelRegion: '',
      executeModel: customModelId,
      executeModelRegion: '',
    })
  }

  /** A model's region list when it genuinely offers a choice, else null. */
  const regionChoices = (id: string | undefined): string[] | null => {
    const m = id ? models.find((x) => x.id === id) : undefined
    const regions = (m as { regions?: string[] } | undefined)?.regions
    return regions && regions.length > 1 ? regions : null
  }

  /**
   * The select value for a row's stored model id: region-qualified for
   * multi-region models — the row's OWN region choice (`<mode>ModelRegion`,
   * legacy per-model map as fallback) when valid, else the model's default
   * region (the FIRST listed, mirroring `effectiveModelRegion`).
   */
  const selectValueFor = (id: string | undefined, regionKey: ModelRegionKey): string => {
    if (!id) return ''
    const regions = regionChoices(id)
    if (!regions) return id
    const chosen = aiSettings[regionKey] || aiSettings.modelRegions?.[id]
    const effective = chosen && regions.includes(chosen) ? chosen : regions[0]
    return `${id}${REGION_SEP}${effective}`
  }

  /** Parse a (possibly region-qualified) option value back to id + region. */
  const parseSelectValue = (raw: string): { id: string; region?: string } => {
    const sep = raw.lastIndexOf(REGION_SEP)
    if (sep === -1) return { id: raw }
    return { id: raw.slice(0, sep), region: raw.slice(sep + 1) }
  }

  /** Persist a row change; the region rides in the row's OWN region key (and
   * is cleared for single-region picks) so no other row is ever affected. */
  const handleModelChange = (key: ModelSettingKey, raw: string): void => {
    const regionKey: ModelRegionKey = `${key}Region`
    if (!raw) {
      patchAiSettings({ [key]: '', [regionKey]: '' })
      return
    }
    const { id, region } = parseSelectValue(raw)
    patchAiSettings({ [key]: id, [regionKey]: region ?? '' })
  }

  // Same per-option gating as the project settings modal: custom
  // (bring-your-own AI) models are selectable on every tier, and free tiers
  // additionally keep the MODE'S OWN free default even when it isn't `freeTier`
  // — but only at its free regions (`freeTierRegions`: a model's cheap host
  // may be a mode's free default while its pricier re-host and every other
  // mode's rows stay paid-only). A model
  // offering several processing regions gets ONE OPTION PER REGION, labeled
  // with the region code — its default region first (the catalog's first
  // entry). Gating is PER ROW because the carve-out is per mode.
  const optionsForRow = (
    defaultId: string | undefined,
  ): { value: string; label: string; disabled: boolean }[] => {
    const freeAllowedAt = (m: (typeof models)[number], region: string): boolean =>
      !!m.freeTier ||
      (m.id === defaultId &&
        !!(m as { freeTierRegions?: string[] }).freeTierRegions?.includes(region))
    return models.flatMap((m) => {
      const paidOnly = !isPro && m.provider !== 'custom'
      const regions = regionChoices(m.id)
      if (!regions) {
        const defaultRegion = (m as { regions?: string[] }).regions?.[0] ?? 'us'
        return [
          { value: m.id, label: m.label, disabled: paidOnly && !freeAllowedAt(m, defaultRegion) },
        ]
      }
      return regions.map((region) => ({
        value: `${m.id}${REGION_SEP}${region}`,
        label: `${m.label} (${region.toUpperCase()})`,
        disabled: paidOnly && !freeAllowedAt(m, region),
      }))
    })
  }
  // Strictly PER-MODE rows: the former account-wide "Default model" was
  // removed — every AI call is always in a mode, so a modeless account knob
  // only added a confusing extra fallback tier. Each select shows the ACTUAL
  // effective model (the stored choice, else the tier's per-mode default) —
  // there is no abstract "Default (…)" option to decode.
  const modelRows: {
    key: ModelSettingKey
    molId: string
    label: string
    defaultId?: string
  }[] = [
    {
      key: 'planModel',
      molId: 'account-ai-plan-model',
      label: t('workspace.settings.planModel.label', undefined, {
        defaultValue: 'Plan-mode model',
      }),
      defaultId: serverDefaults?.plan,
    },
    {
      key: 'executeModel',
      molId: 'account-ai-execute-model',
      label: t('workspace.settings.executeModel.label', undefined, {
        defaultValue: 'Execute-mode model',
      }),
      defaultId: serverDefaults?.execute,
    },
    {
      key: 'commitModel',
      molId: 'account-ai-commit-model',
      label: t('workspace.settings.commitModel.label', undefined, {
        defaultValue: 'Commit-message model',
      }),
      defaultId: serverDefaults?.commit,
    },
    {
      key: 'compactModel',
      molId: 'account-ai-compact-model',
      label: t('workspace.settings.compactModel.label', undefined, {
        defaultValue: 'Compaction model',
      }),
      defaultId: serverDefaults?.compact,
    },
  ]

  return (
    <div className={cm.stack(4)} data-mol-id="account-ai-section">
      <div>
        <p className={cm.cn(cm.textSize('xs'), cm.textMuted, cm.sp('mb', 2))}>
          {t('settings.ai.defaultsHint', undefined, {
            defaultValue: 'Defaults for projects you own. Project settings override these.',
          })}
        </p>
        {!isPro && (
          <p
            className={cm.cn(cm.textSize('xs'), cm.textMuted, cm.sp('mb', 4))}
            data-mol-id="account-ai-tier-note"
          >
            {t('settings.ai.freeTierNote', undefined, {
              defaultValue:
                'Model choice is limited on the free tier — upgrade for access to stronger models.',
            })}
          </p>
        )}
        {/* Label ABOVE a full-width select per row — the old label+select on one
            line left each select a different, cramped width in the narrow panel. */}
        <div className={cm.stack(3)}>
          {modelRows.map((row) => (
            <div key={row.key}>
              <label htmlFor={row.molId} className={cm.formLabelSmall}>
                {row.label}
              </label>
              <Select
                id={row.molId}
                size="sm"
                value={selectValueFor(aiSettings[row.key] || row.defaultId, `${row.key}Region`)}
                onValueChange={(v) => handleModelChange(row.key, v)}
                data-mol-id={row.molId}
                options={optionsForRow(row.defaultId)}
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 className={cm.cn(cm.textSize('sm'), cm.fontWeight('medium'), cm.sp('mb', 1))}>
          {t('settings.ai.providersHeading', undefined, { defaultValue: 'Your AI providers' })}
        </h4>
        <AiProviderManager
          basePath="/account/ai-provider"
          molIdPrefix="account-ai-provider"
          onChanged={handleProvidersChanged}
          // "Use" here sets the account-level plan+execute defaults (there is no
          // per-project scope in account settings) — see handleUseModel.
          onUseModel={handleUseModel}
        />
      </div>

      <ModelsCatalogRefetch key={modelsEpoch} />
    </div>
  )
}
